// Création / édition d'un événement d'agenda (description WYSIWYG + affiche optionnelle)
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import RichTextEditor from "@/components/RichTextEditor";
import { DatePickerFr } from "@/components/DatePickerFr";
import api, { apiError } from "@/lib/api";
import { CATEGORIES_AGENDA } from "@/lib/constants";

const VIDE = {
  titre: "", categorie: "asbl", date_debut: "", date_fin: "", lieu: "",
  description_html: "", infos_pratiques: "", notes_internes: "", public: true,
  recurrence_frequence: "aucune", recurrence_jusqu_au: "",
};

export const EventDialog = ({ open, onClose, event, defaultDate, categoriesAutorisees, onSaved }) => {
  const [form, setForm] = useState(VIDE);
  const [fichier, setFichier] = useState(null);
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFichier(null);
    if (event) {
      setForm({
        titre: event.titre || "", categorie: event.categorie, date_debut: event.date_debut || "",
        date_fin: event.date_fin || "", lieu: event.lieu || "",
        description_html: event.description_html || "", infos_pratiques: event.infos_pratiques || "",
        notes_internes: event.notes_internes || "", public: event.public !== false,
        recurrence_frequence: event.recurrence_frequence || "aucune",
        recurrence_jusqu_au: event.recurrence_jusqu_au || "",
      });
    } else {
      setForm({
        ...VIDE,
        categorie: categoriesAutorisees?.[0] || "asbl",
        date_debut: defaultDate ? `${defaultDate}T19:00` : "",
      });
    }
  }, [open, event, defaultDate, categoriesAutorisees]);

  const sauver = async (e) => {
    e.preventDefault();
    setEnvoi(true);
    const cible = event?.serie_id || event?.id;
    try {
      const payload = { ...form, date_fin: form.date_fin || null, recurrence_jusqu_au: form.recurrence_jusqu_au || null };
      const { data } = event
        ? await api.patch(`/events/${cible}`, payload)
        : await api.post("/events", payload);
      if (fichier) {
        const fd = new FormData();
        fd.append("fichier", fichier);
        await api.post(`/events/${data.id}/image`, fd);
      }
      toast.success(event ? "Événement mis à jour" : "Événement ajouté à l'agenda");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setEnvoi(false);
    }
  };

  const supprimer = async () => {
    if (!window.confirm(event.recurrence_frequence && event.recurrence_frequence !== "aucune"
      ? "Supprimer toute la série d'événements récurrents ?" : "Supprimer cet événement ?")) return;
    try {
      await api.delete(`/events/${event.serie_id || event.id}`);
      toast.success("Événement supprimé");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const categories = (categoriesAutorisees?.length ? categoriesAutorisees : Object.keys(CATEGORIES_AGENDA));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="event-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{event ? "Modifier l'événement" : "Nouvel événement"}</DialogTitle>
          <DialogDescription>Ce qui est marqué « visible publiquement » apparaît sur le lien de partage.</DialogDescription>
        </DialogHeader>
        {event?.occurrence && (
          <p className="text-xs rounded-xl bg-accent/15 border border-accent/40 px-4 py-2.5" data-testid="event-serie-notice">
            Cette date fait partie d'une série récurrente : les modifications s'appliqueront à toute la série.
          </p>
        )}
        <form onSubmit={sauver} className="space-y-4">
          <div>
            <Label>Titre *</Label>
            <Input required value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} placeholder="Ex. Répétition générale Petero Nzukira" className="rounded-xl mt-1.5" data-testid="event-titre-input" />
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label>Catégorie *</Label>
              <Select value={form.categorie} onValueChange={(v) => setForm({ ...form, categorie: v })}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="event-categorie-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map((k) => <SelectItem key={k} value={k}>{CATEGORIES_AGENDA[k].label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Début *</Label>
              <Input type="datetime-local" required value={form.date_debut} onChange={(e) => setForm({ ...form, date_debut: e.target.value })} className="rounded-xl mt-1.5" data-testid="event-debut-input" />
            </div>
            <div>
              <Label>Fin</Label>
              <Input type="datetime-local" value={form.date_fin} onChange={(e) => setForm({ ...form, date_fin: e.target.value })} className="rounded-xl mt-1.5" data-testid="event-fin-input" />
            </div>
          </div>
          <div>
            <Label>Lieu</Label>
            <Input value={form.lieu} onChange={(e) => setForm({ ...form, lieu: e.target.value })} placeholder="Ex. Cirque Royal, Bruxelles" className="rounded-xl mt-1.5" data-testid="event-lieu-input" />
          </div>
          <div>
            <Label>Description publique</Label>
            <div className="mt-1.5">
              <RichTextEditor
                value={form.description_html}
                onChange={(html) => setForm((f) => ({ ...f, description_html: html }))}
                placeholder="Présentation de l'événement telle qu'elle sera vue publiquement…"
              />
            </div>
          </div>
          <div>
            <Label>Informations importantes (visibles publiquement)</Label>
            <Textarea rows={3} value={form.infos_pratiques} onChange={(e) => setForm({ ...form, infos_pratiques: e.target.value })} placeholder="Tenue exigée, heure d'arrivée, entrée, PAF…" className="rounded-xl mt-1.5" data-testid="event-infos-input" />
          </div>
          <div>
            <Label>Notes internes (jamais publiées)</Label>
            <Textarea rows={2} value={form.notes_internes} onChange={(e) => setForm({ ...form, notes_internes: e.target.value })} className="rounded-xl mt-1.5" data-testid="event-notes-input" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Récurrence</Label>
              <Select value={form.recurrence_frequence} onValueChange={(v) => setForm({ ...form, recurrence_frequence: v })}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="event-recurrence-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="aucune">Événement unique</SelectItem>
                  <SelectItem value="hebdomadaire">Chaque semaine</SelectItem>
                  <SelectItem value="bimensuelle">Toutes les 2 semaines</SelectItem>
                  <SelectItem value="mensuelle">Chaque mois</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.recurrence_frequence !== "aucune" && (
              <div>
                <Label>Jusqu'au</Label>
                <div className="mt-1.5">
                  <DatePickerFr
                    value={form.recurrence_jusqu_au}
                    onChange={(v) => setForm((f) => ({ ...f, recurrence_jusqu_au: v }))}
                    placeholder="Pas de date de fin"
                    testId="event-recurrence-fin-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">Sans date de fin, la répétition s'arrête après un an.</p>
              </div>
            )}
          </div>
          <div>
            <Label>Affiche (optionnelle, 5 Mo max)</Label>
            <label className="mt-1.5 flex items-center gap-3 rounded-xl border border-dashed border-border px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors">
              <Upload className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground truncate">{fichier ? fichier.name : "Choisir une image (JPG, PNG, WebP)"}</span>
              <input type="file" accept="image/*" className="hidden" onChange={(e) => setFichier(e.target.files?.[0] || null)} data-testid="event-image-input" />
            </label>
          </div>
          <label className="flex items-center gap-3 rounded-xl border border-border px-4 py-3">
            <Switch checked={form.public} onCheckedChange={(v) => setForm({ ...form, public: v })} data-testid="event-public-switch" />
            <span className="text-sm">Visible sur le lien public partageable</span>
          </label>
          <DialogFooter className="gap-2">
            {event && (
              <Button type="button" variant="ghost" onClick={supprimer} className="rounded-full text-destructive gap-1.5 mr-auto" data-testid="event-delete-button">
                <Trash2 className="h-4 w-4" /> Supprimer
              </Button>
            )}
            <Button type="submit" disabled={envoi} className="rounded-full" data-testid="event-save-button">
              {envoi ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
